import {
  Alert,
  AlertTitle,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import CorporateSplitPage from '../../../../components/SplitPage/CorporateSplitPage'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import API from '../../../../services/API'
import LinkIcon from '@mui/icons-material/Link'

const PRIVACY_NOTE = (
  <Typography variant="body2" color="text.secondary">
    We use feedback to review and improve the care we provide. Your name and
    contact details are only stored if you choose to give them, and are never
    shared or displayed. See our{' '}
    <Link to="/privacy-policy" target="_blank" rel="noreferrer">
      privacy policy
    </Link>{' '}
    for more information.
  </Typography>
)

const INVITE_MESSAGES = {
  expired:
    'This feedback link has expired, but you can still leave feedback anonymously below.',
  used: 'Feedback has already been left using this link. You can still leave further feedback anonymously below.',
  invalid:
    "We couldn't recognise this feedback link, but you can still leave feedback anonymously below.",
  locked:
    'Too many attempts. Please try again in 15 minutes, or leave feedback anonymously.',
}

// Identity gate shown when the page is opened from an SMS invite link.
// Confirming links the feedback to the care record; declining hands the
// patient straight to the plain anonymous form.
const IdentityGate = ({ token, initialState, onVerified, onAnonymous }) => {
  const [lastName, setLastName] = useState('')
  const [dob, setDob] = useState('')
  const [busy, setBusy] = useState(false)
  // { state, attemptsLeft } — pre-populated when the link is already locked
  const [result, setResult] = useState(
    initialState && initialState !== 'open' ? { state: initialState } : null,
  )

  const handleVerify = (e) => {
    e.preventDefault()
    setBusy(true)
    API.post('/patient-feedback/verify', { token, lastName, dob })
      .then((res) => {
        if (res.status === 200 && res.data?.ok) {
          onVerified(res.data.verifyKey)
        } else {
          const state = res.data?.state
          setResult(
            state && (state === 'no-match' || INVITE_MESSAGES[state])
              ? res.data
              : { state: 'invalid' },
          )
        }
      })
      .catch(() => setResult({ state: 'invalid' }))
      .finally(() => setBusy(false))
  }

  const blocked = result && result.state !== 'no-match'

  return (
    <Stack gap={3} component="form" onSubmit={handleVerify}>
      <Alert severity="info" icon={false}>
        <Typography variant="title3">
          Thank you for taking the time to give us feedback about your care.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          If you confirm the <strong>patient's</strong> surname and date of
          birth, we can link your feedback to the care record so it reaches
          the crew who looked after you. If you'd rather not, choose{' '}
          <strong>Leave feedback anonymously</strong> and no link is kept.
        </Typography>
      </Alert>

      {result?.state === 'no-match' && (
        <Alert severity="warning">
          <AlertTitle>No match found</AlertTitle>
          Those details don't match the care record for this link. Please
          check and try again
          {typeof result.attemptsLeft === 'number' &&
            ` (${result.attemptsLeft} attempt${
              result.attemptsLeft === 1 ? '' : 's'
            } remaining)`}
          , or leave your feedback anonymously.
        </Alert>
      )}
      {blocked && (
        <Alert severity="warning">{INVITE_MESSAGES[result.state]}</Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <TextField
            label="Patient's surname"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            fullWidth
            disabled={blocked}
            autoComplete="off"
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <TextField
            label="Patient's date of birth"
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            required
            fullWidth
            disabled={blocked}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <Button
            type="submit"
            color="success"
            variant="contained"
            fullWidth
            disabled={busy || blocked}
          >
            Confirm and continue
          </Button>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Button
            variant="outlined"
            color="anBlue"
            fullWidth
            onClick={onAnonymous}
          >
            Leave feedback anonymously
          </Button>
        </Grid>
      </Grid>
      {PRIVACY_NOTE}
    </Stack>
  )
}

const PatientFeedback = () => {
  const { token } = useParams()
  // 'checking' | 'gate' | 'form'
  const [step, setStep] = useState(token ? 'checking' : 'form')
  const [inviteState, setInviteState] = useState(null)
  const [verifyKey, setVerifyKey] = useState(null)
  const [linkExpired, setLinkExpired] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!token) {
      setStep('form')
      setVerifyKey(null)
      setInviteState(null)
      return
    }
    API.get(`/patient-feedback/invite/${token}`)
      .then((res) => {
        const state = res.status === 200 ? res.data?.state : 'invalid'
        setInviteState(state)
        setStep(state === 'open' || state === 'locked' ? 'gate' : 'form')
      })
      .catch(() => {
        setInviteState('invalid')
        setStep('form')
      })
  }, [token])

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [emailValidationError, setEmailValidationError] = useState(false)
  const [phone, setPhone] = useState('')
  const [phoneValidationError, setPhoneValidationError] = useState(false)
  const [contactType, setContactType] = useState('')
  const [relationship, setRelationship] = useState('')
  const [dignityRespect, setDignityRespect] = useState(false)
  const [involved, setInvolved] = useState(false)
  const [kindHelpful, setKindHelpful] = useState(false)
  const [confidenceTrust, setConfidenceTrust] = useState(false)
  const [ambulanceCare, setAmbulanceCare] = useState('')
  const [recommend, setRecommend] = useState('')
  const [feedbackComments, setFeedbackComments] = useState('')
  const [mayShare, setMayShare] = useState('')
  const [mayContact, setMayContact] = useState('')

  const theme = useTheme()

  const navigate = useNavigate()

  const useQuery = () => {
    const { search } = useLocation()
    return useMemo(() => new URLSearchParams(search), [search])
  }

  const query = useQuery()
  const thankyou = query.get('status')
  const wasLinked = query.get('linked') === '1'

  const submit = (withLink) => {
    setSubmitting(true)
    API.post('/patient-feedback', {
      firstName,
      lastName,
      email,
      phone,
      contactType,
      relationship,
      dignityRespect,
      involved,
      kindHelpful,
      confidenceTrust,
      ambulanceCare,
      recommend,
      feedbackComments,
      mayShare,
      mayContact,
      ...(withLink && verifyKey ? { verifyKey } : {}),
    })
      .then((res) => {
        if (res.status === 200) {
          navigate(`?status=submitted${withLink && verifyKey ? '&linked=1' : ''}`)
        } else if (res.status === 409) {
          // Verification window lapsed — keep the form, offer anonymous.
          setLinkExpired(true)
        }
      })
      .catch((e) => console.error(e))
      .finally(() => setSubmitting(false))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    submit(true)
  }

  const goAnonymous = () => {
    setVerifyKey(null)
    setLinkExpired(false)
    setInviteState(null)
    setStep('form')
    navigate('/patient-feedback', { replace: true })
  }

  return (
    <CorporateSplitPage pageTitle="Patient Feedback">
      {thankyou === 'submitted' ? (
        <Alert severity="success">
          <AlertTitle>Thank You</AlertTitle>
          Your feedback has been submitted
          {wasLinked && ' and linked to your care record'}. You may now close
          this page.
        </Alert>
      ) : step === 'checking' ? (
        <Alert severity="info" icon={false}>
          Checking your feedback link…
        </Alert>
      ) : step === 'gate' ? (
        <IdentityGate
          token={token}
          initialState={inviteState}
          onVerified={(key) => {
            setVerifyKey(key)
            setStep('form')
          }}
          onAnonymous={goAnonymous}
        />
      ) : (
        <Stack gap={4} component="form" onSubmit={handleSubmit}>
          <Alert severity="info" icon={false}>
            <Typography variant="title3">
              Your feedback is so important to us, and is an integral way of
              ensuring our services are delivered to the highest possible
              standards.
            </Typography>
            <Typography variant="title3">
              If you, or a loved one, have recently received care from us, we'd
              be extremely grateful for your feedback.
            </Typography>
          </Alert>
          {verifyKey ? (
            <Alert severity="success" icon={<LinkIcon fontSize="inherit" />}>
              Thank you — this feedback will be linked to your care record so
              it reaches the crew who looked after you.
            </Alert>
          ) : inviteState && INVITE_MESSAGES[inviteState] ? (
            <Alert severity="warning">{INVITE_MESSAGES[inviteState]}</Alert>
          ) : (
            <Alert severity="info" variant="outlined">
              This feedback is anonymous and is not linked to any care record.
            </Alert>
          )}
          {linkExpired && (
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setVerifyKey(null)
                    setLinkExpired(false)
                    submit(false)
                  }}
                >
                  Submit anonymously
                </Button>
              }
            >
              Your identity confirmation has expired, so we can't link this
              feedback to your care record. You can still submit it
              anonymously.
            </Alert>
          )}
          {/* TODO: CQC Link */}
          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <FormControl fullWidth>
                <InputLabel>Where did you meet us?</InputLabel>
                <Select
                  value={contactType}
                  required
                  onChange={(e) => setContactType(e.target.value)}
                  label="Where did you meet us?"
                >
                  <MenuItem value="At an Event">At an Event</MenuItem>
                  <MenuItem value="Hospital">Hospital</MenuItem>
                  <MenuItem value="My Home">My Home</MenuItem>
                  <MenuItem value="Somewhere else">Somewhere else</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={6}>
              <FormControl fullWidth>
                <InputLabel>What is your relationship?</InputLabel>
                <Select
                  label="What is your relationship?"
                  value={relationship}
                  required
                  onChange={(e) => setRelationship(e.target.value)}
                >
                  <MenuItem value="Patient">I am the patient</MenuItem>
                  <MenuItem value="Relative">I am a Relative/Friend</MenuItem>
                  <MenuItem value="Carer">I am a Carer</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Stack>
                <Typography variant="title3">Select all that apply:</Typography>
                <FormControlLabel
                  label="I was treated with Dignity & Respect"
                  control={
                    <Switch
                      checked={dignityRespect}
                      onChange={(e) => setDignityRespect(e.target.checked)}
                      color="success"
                    />
                  }
                />
                <FormControlLabel
                  label="I was involved in the decisions made about my care"
                  control={
                    <Switch
                      checked={involved}
                      onChange={(e) => setInvolved(e.target.checked)}
                      color="success"
                    />
                  }
                />
                <FormControlLabel
                  label="The staff were kind and helpful"
                  control={
                    <Switch
                      checked={kindHelpful}
                      onChange={(e) => setKindHelpful(e.target.checked)}
                      color="success"
                    />
                  }
                />
                <FormControlLabel
                  label="I had confidence and trust in the medical staff"
                  control={
                    <Switch
                      checked={confidenceTrust}
                      onChange={(e) => setConfidenceTrust(e.target.checked)}
                      color="success"
                    />
                  }
                />
              </Stack>
            </Grid>
            <Grid item xs={12} lg={6}>
              <FormControl fullWidth>
                <InputLabel>
                  Were you treated or transported in an ambulance?
                </InputLabel>
                <Select
                  label="Were you treated or transported in an ambulance?"
                  value={ambulanceCare}
                  onChange={(e) => setAmbulanceCare(e.target.value)}
                  color="anBlue"
                >
                  <MenuItem value={true}>Yes</MenuItem>
                  <MenuItem value={false}>No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={6}>
              <FormControl fullWidth>
                <InputLabel>
                  Would you recommend us to your friends and family?
                </InputLabel>
                <Select
                  label="Would you recommend us to your friends and family?"
                  value={recommend}
                  required
                  onChange={(e) => setRecommend(e.target.value)}
                  color="anBlue"
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                  <MenuItem value="Unsure">Unsure</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Is there any other feedback you'd like to pass on?"
                value={feedbackComments}
                onChange={(e) => setFeedbackComments(e.target.value)}
                multiline
                minRows={2}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <FormControl fullWidth>
                <InputLabel>
                  Would you be happy for us to share the feedback given to
                  others?
                </InputLabel>
                <Select
                  label="Would you be happy for us to share the feedback given to others?"
                  value={mayShare}
                  onChange={(e) => setMayShare(e.target.value)}
                  color="anBlue"
                >
                  <MenuItem value={true}>Yes</MenuItem>
                  <MenuItem value={false}>No</MenuItem>
                </Select>
                <FormHelperText>
                  Your name and personal details will never be shared or
                  displayed
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={6}>
              <FormControl fullWidth>
                <InputLabel>
                  May we contact you to discuss your feedback if required?
                </InputLabel>
                <Select
                  label="May we contact you to discuss your feedback if required?"
                  value={mayContact}
                  onChange={(e) => setMayContact(e.target.value)}
                  color="anBlue"
                >
                  <MenuItem value={true}>Yes</MenuItem>
                  <MenuItem value={false}>No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {mayContact && (
              <>
                <Grid item xs={12} lg={4}>
                  <TextField
                    label="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    fullWidth
                    required={mayContact}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <TextField
                    label="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    fullWidth
                    required={mayContact}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <TextField
                    label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={emailValidationError}
                    required={mayContact}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <TextField
                    label="Phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    error={phoneValidationError}
                    fullWidth
                    required={mayContact}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <Button
                type="submit"
                color="success"
                fullWidth
                variant="contained"
                disabled={submitting}
              >
                Submit
              </Button>
            </Grid>
          </Grid>
          {PRIVACY_NOTE}
        </Stack>
      )}
    </CorporateSplitPage>
  )
}

export default PatientFeedback
